import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/solid';

import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { downloadReport } from '../utils/downloadReport';

const currentYear = new Date().getFullYear();
const currentMonth = String(new Date().getMonth() + 1);
const minAdminCollectionsDate = '2026-02-10';
const minAdminCollectionsYear = 2026;

const monthOptions = [
  ['1', 'Enero'],
  ['2', 'Febrero'],
  ['3', 'Marzo'],
  ['4', 'Abril'],
  ['5', 'Mayo'],
  ['6', 'Junio'],
  ['7', 'Julio'],
  ['8', 'Agosto'],
  ['9', 'Septiembre'],
  ['10', 'Octubre'],
  ['11', 'Noviembre'],
  ['12', 'Diciembre'],
];

const tabs = [
  { key: 'tramites_pendientes_cobro', label: 'Tramites pendientes' },
  { key: 'tramites_no_justificados', label: 'No justificados' },
  { key: 'pagos_extras_pendientes', label: 'Extras pendientes' },
  { key: 'creditos_pendientes_cobro', label: 'Creditos por cobrar' },
];

const toMoney = (value) => {
  const number = Number(value);
  return `$${Number.isFinite(number) ? number.toFixed(2) : '0.00'}`;
};

const AdminCollectionsSummary = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const hasCollectionsPermission = user?.role === 'notario' || user?.is_superuser;
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [matrizadores, setMatrizadores] = useState([]);
  const [activeTab, setActiveTab] = useState('tramites_pendientes_cobro');
  const [showMatrizadorSummary, setShowMatrizadorSummary] = useState(false);
  const summaryRequestRef = useRef(0);
  const [filters, setFilters] = useState({
    period: 'month',
    date: '',
    year: String(currentYear),
    month: currentMonth,
    start_date: '',
    end_date: '',
    usuario_matrizador: '',
  });

  const availableMonthOptions = useMemo(() => {
    if (Number(filters.year) === minAdminCollectionsYear) {
      return monthOptions.filter(([value]) => Number(value) >= 2);
    }
    return monthOptions;
  }, [filters.year]);

  useEffect(() => {
    if (Number(filters.year) === minAdminCollectionsYear && Number(filters.month) < 2) {
      setFilters((prev) => ({ ...prev, month: '2' }));
    }
  }, [filters.year, filters.month]);

  useEffect(() => {
    if (!loading && !hasCollectionsPermission) {
      toast.error('No tienes permisos para acceder al resumen administrativo.');
      navigate('/login');
    }
  }, [hasCollectionsPermission, loading, navigate]);

  const filtersComplete = useMemo(() => {
    if (filters.period === 'day') return Boolean(filters.date);
    if (filters.period === 'range') {
      return Boolean(
        filters.start_date
        && filters.end_date
        && filters.start_date <= filters.end_date
      );
    }
    return Boolean(filters.year && filters.month);
  }, [filters]);

  const buildParams = useCallback(() => {
    const params = { period: filters.period };
    if (filters.usuario_matrizador) params.usuario_matrizador = filters.usuario_matrizador;

    if (filters.period === 'day') {
      if (filters.date) params.date = filters.date;
      return params;
    }
    if (filters.period === 'range') {
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      return params;
    }
    params.year = filters.year;
    params.month = filters.month;
    return params;
  }, [filters]);

  const fetchSummary = useCallback(async () => {
    const requestId = summaryRequestRef.current + 1;
    summaryRequestRef.current = requestId;
    if (!filtersComplete) {
      setSummary(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.get('/api/audit/reports/admin-collections-summary/', {
        params: buildParams(),
      });
      if (requestId !== summaryRequestRef.current) return;
      setSummary(response.data || null);
    } catch (error) {
      if (requestId !== summaryRequestRef.current) return;
      toast.error(error?.response?.data?.error || 'No se pudo cargar el resumen administrativo.');
      setSummary(null);
    } finally {
      if (requestId === summaryRequestRef.current) {
        setIsLoading(false);
      }
    }
  }, [buildParams, filtersComplete]);

  useEffect(() => {
    if (!loading && hasCollectionsPermission) {
      fetchSummary();
    }
  }, [loading, hasCollectionsPermission, fetchSummary]);

  useEffect(() => {
    const fetchMatrizadores = async () => {
      try {
        const response = await api.get('/api/audit/matrizadores/list/');
        const rows = Array.isArray(response.data) ? response.data : [];
        setMatrizadores(rows.map((row) => String(row?.nombre || '').trim()).filter(Boolean));
      } catch (error) {
        setMatrizadores([]);
      }
    };

    if (!loading && hasCollectionsPermission) {
      fetchMatrizadores();
    }
  }, [loading, hasCollectionsPermission]);

  const matrizadorOptions = useMemo(() => {
    const values = [
      ...matrizadores,
      ...(Array.isArray(summary?.by_matrizador) ? summary.by_matrizador.map((row) => row.label) : []),
    ];
    return Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [matrizadores, summary]);

  const detailRows = useMemo(() => {
    const rows = Array.isArray(summary?.rows) ? summary.rows : [];
    return rows.filter((row) => row.category === activeTab);
  }, [summary, activeTab]);

  const handleDownload = () => {
    if (!filtersComplete) return;
    downloadReport({
      api,
      endpoint: '/api/audit/reports/admin-collections-summary/',
      params: { ...buildParams(), category: activeTab },
      format: 'xlsx',
      baseName: `resumen-admin-cobros-${activeTab}`,
    });
  };

  if (loading) return <div className="text-white text-center">Cargando...</div>;
  if (!hasCollectionsPermission) return null;

  const totals = summary?.totals || {};

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-gray-900 p-4 text-white sm:p-6">
      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />

      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Notaria 4 Canton Otavalo" className="w-12 h-auto sm:w-14" />
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">Resumen Administrativo de Cobros</h1>
            <p className="text-sm text-gray-300">{summary?.period?.label || 'Periodo seleccionado'}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={fetchSummary}
            disabled={!filtersComplete}
            className="inline-flex items-center gap-2 rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3"
          >
            <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!summary || !filtersComplete}
            className="inline-flex items-center gap-2 rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white disabled:opacity-50"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            Descargar XLSX
          </button>
          <Link to="/dashboard/notario" className="rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3">
            Volver
          </Link>
          <Link to="/admin-cobros/calendario" className="inline-flex items-center gap-2 rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3">
            <CalendarDaysIcon className="h-5 w-5 text-notaria-yellow" />
            Ver calendario de cumplimiento
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6">
        <section aria-label="Totales pendientes de cobro" className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <MetricCard label="Total rojos al contado por justificar" value={toMoney(totals.tramites_no_justificados?.total)} count={`${totals.tramites_no_justificados?.count || 0} registros`} valueClassName="text-red-300" />
          <MetricCard label="Total extras pendientes" value={toMoney(totals.pagos_extras_pendientes?.total)} count={`${totals.pagos_extras_pendientes?.count || 0} registros`} />
          <MetricCard label="Total créditos por cobrar" value={toMoney(totals.creditos_pendientes_cobro?.total)} count={`${totals.creditos_pendientes_cobro?.count || 0} registros`} />
        </section>

        <section className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="min-w-0 flex-1 rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <select
                value={filters.period}
                onChange={(e) => setFilters((prev) => ({ ...prev, period: e.target.value }))}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                aria-label="Periodo"
              >
                <option value="month">Mes</option>
                <option value="day">Dia</option>
                <option value="range">Rango</option>
              </select>

              {filters.period === 'day' && (
                <input
                  type="date"
                  min={minAdminCollectionsDate}
                  value={filters.date}
                  onChange={(e) => setFilters((prev) => ({ ...prev, date: e.target.value }))}
                  className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                  aria-label="Dia"
                />
              )}

              {filters.period === 'month' && (
                <>
                  <input
                    type="number"
                    min={minAdminCollectionsYear}
                    value={filters.year}
                    onChange={(e) => setFilters((prev) => ({ ...prev, year: e.target.value }))}
                    className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                    aria-label="Anio"
                  />
                  <select
                    value={filters.month}
                    onChange={(e) => setFilters((prev) => ({ ...prev, month: e.target.value }))}
                    className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                    aria-label="Mes"
                  >
                    {availableMonthOptions.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </>
              )}

              {filters.period === 'range' && (
                <>
                  <input
                    type="date"
                    min={minAdminCollectionsDate}
                    value={filters.start_date}
                    onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
                    className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                    aria-label="Desde"
                  />
                  <input
                    type="date"
                    min={filters.start_date || minAdminCollectionsDate}
                    value={filters.end_date}
                    onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
                    className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                    aria-label="Hasta"
                  />
                </>
              )}

              <select
                value={filters.usuario_matrizador}
                onChange={(e) => setFilters((prev) => ({ ...prev, usuario_matrizador: e.target.value }))}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                aria-label="Matrizador"
              >
                <option value="">Todos los matrizadores</option>
                {matrizadorOptions.map((nombre) => (
                  <option key={nombre} value={nombre}>{nombre}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={fetchSummary}
                disabled={!filtersComplete}
                className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white"
              >
                Aplicar
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowMatrizadorSummary((visible) => !visible)}
            aria-expanded={showMatrizadorSummary}
            aria-controls="admin-collections-by-matrizador"
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold text-white hover:bg-notaria-blue-3 xl:w-auto xl:min-w-[300px]"
          >
            <CalendarDaysIcon className="h-5 w-5 text-notaria-yellow" />
            {showMatrizadorSummary ? 'Ocultar consolidado por matrizador' : 'Ver consolidado por matrizador'}
            {showMatrizadorSummary ? (
              <ChevronUpIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </button>
        </section>

        {showMatrizadorSummary && (
          <section id="admin-collections-by-matrizador" className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
            <div className="mb-3 flex items-center gap-2 text-lg font-semibold">
              <CalendarDaysIcon className="h-5 w-5 text-notaria-yellow" />
              Consolidado por matrizador
            </div>
            <SummaryTable rows={summary?.by_matrizador || []} emptyText="No hay matrizadores con valores pendientes." />
          </section>
        )}

        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          <div className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-lg px-3 py-2 text-sm font-bold transition ${
                  activeTab === tab.key ? 'bg-notaria-yellow text-notaria-blue-1' : 'bg-notaria-blue-2/70 text-white hover:bg-notaria-blue-3'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <DetailTable rows={detailRows} isLoading={isLoading} />
        </section>
      </main>
    </motion.div>
  );
};

const MetricCard = ({ icon, label, value, count, valueClassName = 'text-notaria-yellow' }) => (
  <article aria-label={label} className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-lg">
    <div className="mb-2 flex items-center justify-between gap-3">
      <p className="text-sm text-gray-300">{label}</p>
      {icon || null}
    </div>
    <p className={`text-2xl font-bold ${valueClassName}`}>{value}</p>
    <p className="text-sm text-gray-300">{count}</p>
  </article>
);

const SummaryTable = ({ rows, emptyText }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-notaria-blue-2 text-left">
          <th className="p-3">Nombre</th>
          <th className="p-3">Total abierto</th>
          <th className="p-3">Tramites</th>
          <th className="p-3">No justificados</th>
          <th className="p-3">Extras</th>
          <th className="p-3">Creditos</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan="6" className="p-4 text-center text-gray-400">{emptyText}</td></tr>
        ) : rows.map((row) => (
          <tr key={row.key} className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2/40">
            <td className="p-3 font-semibold">{row.label}</td>
            <td className="p-3 font-semibold text-notaria-yellow">{toMoney(row.total_abierto)}</td>
            <td className="p-3">{row.tramites_pendientes_cobro?.count || 0} / {toMoney(row.tramites_pendientes_cobro?.total)}</td>
            <td className="p-3">{row.tramites_no_justificados?.count || 0} / {toMoney(row.tramites_no_justificados?.total)}</td>
            <td className="p-3">{row.pagos_extras_pendientes?.count || 0} / {toMoney(row.pagos_extras_pendientes?.total)}</td>
            <td className="p-3">{row.creditos_pendientes_cobro?.count || 0} / {toMoney(row.creditos_pendientes_cobro?.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DetailTable = ({ rows, isLoading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(rows.length / pageSize)), [rows.length, pageSize]);
  const pageStart = rows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, rows.length);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rows]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <p className="text-gray-300">
          Mostrando {pageStart}-{pageEnd} de {rows.length} registros
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="admin-collections-page-size" className="text-gray-300">Filas</label>
          <select
            id="admin-collections-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="rounded-lg bg-notaria-blue-2 px-3 py-2 text-white"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
            disabled={currentPage <= 1 || rows.length === 0}
            className={`rounded-lg px-3 py-2 font-semibold ${
              currentPage <= 1 || rows.length === 0
                ? 'bg-notaria-blue-2/50 text-gray-400'
                : 'bg-notaria-blue-2 text-white hover:bg-notaria-blue-3'
            }`}
          >
            Anterior
          </button>
          <span className="px-2 text-gray-200">Pagina {currentPage} de {totalPages}</span>
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
            disabled={currentPage >= totalPages || rows.length === 0}
            className={`rounded-lg px-3 py-2 font-semibold ${
              currentPage >= totalPages || rows.length === 0
                ? 'bg-notaria-blue-2/50 text-gray-400'
                : 'bg-notaria-blue-2 text-white hover:bg-notaria-blue-3'
            }`}
          >
            Siguiente
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-notaria-blue-2 text-left">
              <th className="p-3">Dia</th>
              <th className="p-3">Matrizador</th>
              <th className="p-3">Referencia</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Monto</th>
              <th className="p-3">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="6" className="p-4 text-center text-gray-300">Cargando...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="6" className="p-4 text-center text-gray-400">No hay registros en esta vista.</td></tr>
            ) : paginatedRows.map((row, index) => (
              <tr key={`${row.category}-${row.source_type}-${row.source_id}-${currentPage}-${index}`} className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2/40">
                <td className="p-3">{row.date}</td>
                <td className="p-3">{row.matrizador || '-'}</td>
                <td className="p-3">{row.reference || '-'}</td>
                <td className="p-3"><StatusIndicator status={row.status} /></td>
                <td className="p-3 font-semibold">{toMoney(row.amount)}</td>
                <td className="p-3">
                  <div className="max-h-20 max-w-xl overflow-y-auto text-xs text-gray-200">
                    {typeof row.description === 'string' ? row.description : JSON.stringify(row.description || {})}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
);
};

const StatusIndicator = ({ status }) => {
  const normalized = String(status || '').trim().toLowerCase();
  const colors = {
    rojo: 'bg-red-500',
    amarillo: 'bg-amber-400',
    verde: 'bg-emerald-500',
  };
  if (!colors[normalized]) return <>{status || '-'}</>;
  const label = `Estado ${normalized}`;
  return (
    <span className="inline-flex items-center gap-2">
      <span role="img" aria-label={label} title={label} className={`h-3 w-3 rounded-full ${colors[normalized]}`} />
      <span className="sr-only">{label}</span>
    </span>
  );
};

export default AdminCollectionsSummary;
